// src/components/ScenarioRuns.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography } from '@mui/material';
import { SetFilterModule } from 'ag-grid-enterprise';
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  useNotification,
} from '@1823-partners/core';
import api, { fetchScenarioRuns } from '../api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { notifyUngatedFailure } from '../utils/notifyDenial';

dayjs.extend(utc);
dayjs.extend(timezone);

const ScenarioRuns = ({ configId }) => {
  const { showNotification } = useNotification();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 100;
  const gridRef = useRef(null);

  // Per-user grid layout persistence. We own the hook here (rather than
  // letting GridConfigMenu instantiate it internally) so we can bridge
  // gridConfigs.onGridReady into PamGrid's onGridReady — without that bridge a
  // slow grid that mounts after the configs response would miss the
  // default-layout auto-apply. gridId is unique within the scenarios app.
  const gridConfigs = useGridConfigs(api, {
    app: 'scenarios',
    gridId: 'runs',
    gridApiRef: gridRef,
  });

  const timeFilter = {
    browserDatePicker: true,
    comparator: (filterDate, cellValue) => {
      if (!cellValue) return -1;
      const d = new Date(cellValue);
      if (d < filterDate) return -1;
      if (d > filterDate) return 1;
      return 0;
    },
    inRangeInclusive: true,
  };

  const columnDefs = [
    { headerName: 'Config ID', field: 'CONFIG_ID', filter: false },
    { headerName: 'Scenario', field: 'SCENARIO', filter: 'agSetColumnFilter' },
    { headerName: 'CUSIP', field: 'CUSIP', filter: 'agSetColumnFilter' },
    { headerName: 'Book', field: 'BOOK', filter: 'agSetColumnFilter' },
    {
      headerName: 'Run Date',
      field: 'RUN_DATE',
      filter: 'agDateColumnFilter',
      filterParams: timeFilter,
      valueFormatter: params =>
        params.value
          ? dayjs(params.value).tz('America/New_York').format('M/D/YYYY h:mm:ss A')
          : '',
    },
    {
      headerName: 'Start Time',
      field: 'START_TIME',
      filter: 'agDateColumnFilter',
      filterParams: timeFilter,
      valueFormatter: params =>
        params.value
          ? dayjs(params.value).tz('America/New_York').format('M/D/YYYY h:mm:ss A')
          : '',
    },
    {
      headerName: 'End Time',
      field: 'END_TIME',
      filter: 'agDateColumnFilter',
      filterParams: timeFilter,
      valueFormatter: params =>
        params.value
          ? dayjs(params.value).tz('America/New_York').format('M/D/YYYY h:mm:ss A')
          : '',
    },
    { headerName: 'Elapsed Time', field: 'ELAPSED_TIME' },
    { headerName: 'Status', field: 'STATUS' },
  ];

  useEffect(() => {
    const loadRuns = async (selectedConfigId, isActive) => {
      setLoading(true);
      try {
        const response = await fetchScenarioRuns(selectedConfigId);
        if (!isActive()) return;

        // 1) Grab the payload (axios style or fetch style)
        let payload = response?.data ?? response;

        // 2) If it's a string, sanitize NaN and parse
        if (typeof payload === 'string') {
          // replace any unquoted NaN with null
          const sanitized = payload.replace(/:\s*NaN(?=[,}])/g, ':null');
          try {
            payload = JSON.parse(sanitized);
          } catch (err) {
            console.error('Failed to parse sanitized JSON:', err);
            payload = [];
          }
        }

        // 3) If it's an axios-like response object with nested .data array
        if (
          payload &&
          typeof payload === 'object' &&
          !Array.isArray(payload) &&
          Array.isArray(payload.data)
        ) {
          payload = payload.data;
        }

        // 4) Final array guard
        const runs = Array.isArray(payload) ? payload : [];

        setRowData(runs);
      } catch (error) {
        if (isActive()) {
          console.error('Error fetching scenario runs', error);
          notifyUngatedFailure(showNotification, error);
          setRowData([]);
        }
      } finally {
        if (isActive()) setLoading(false);
      }
    };

    let active = true;
    setRowData([]);
    if (configId) {
      loadRuns(configId, () => active);
    } else {
      setLoading(false);
    }
    return () => { active = false; };
  }, [configId, showNotification]);

  if (!configId) {
    return (
      <Box p={2}>
        <Typography>Please select a scenario config above to view its runs.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Minimal header hosting the saved-layout menu. PamGrid's built-in
          toolbar slot is unused here (showToolbar defaults false), so the
          GridConfigMenu rides in this standalone header instead. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />
      </Box>
      <PamGrid
        ref={gridRef}
        modules={[SetFilterModule]}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={{
          flex: 1,
          sortable: true,
          resizable: true,
          filter: 'agTextColumnFilter',
        }}
        getRowId={(data) =>
          [data.CONFIG_ID, data.RUN_DATE, data.CUSIP, data.SCENARIO, data.BOOK]
            .map((value) => encodeURIComponent(value ?? '')).join('|')
        }
        isLoading={loading}
        loadingMessage="Loading scenario runs..."
        height={400}
        showQuickFilter={false}
        showFooter={false}
        pagination
        paginationPageSize={PAGE_SIZE}
        // Per-user grid layout persistence. Bridge onGridReady so the saved
        // default applies even if the grid mounts after configs load; mirror
        // every column/sort/filter gesture into the local stash. PamGrid
        // surfaces onFilterChanged at the top level; the column events ride
        // through agGridProps as the stashBindings bundle.
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
      />
    </Box>
  );
};

export default ScenarioRuns;
